import { useState } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Check, Printer, Share2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ShareReportDialog({ open, onOpenChange, reportId }) {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const create = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/reports/${reportId}/share`);
      setToken(data.share_token);
    } catch (_e) {
      toast.error("Could not create share link.");
    } finally {
      setLoading(false);
    }
  };

  const revoke = async () => {
    setLoading(true);
    try {
      await api.delete(`/reports/${reportId}/share`);
      setToken(null);
      toast.success("Share link revoked.");
    } catch (_e) {
      toast.error("Could not revoke link.");
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = token ? `${window.location.origin}/shared/${token}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      // Fallback for environments where clipboard API is blocked (iframes / mobile / preview)
      try {
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (_e2) {
        toast.info("Long-press the link to copy.");
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl max-w-md" data-testid="share-report-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
            <Share2 size={20} /> Share with your doctor
          </DialogTitle>
          <DialogDescription>
            Generate a private read-only link. Anyone with the link can view this specific
            report only.
          </DialogDescription>
        </DialogHeader>

        {!token ? (
          <div className="mt-2 space-y-3">
            <p className="text-sm" style={{ color: "#5C6B66" }}>
              You can revoke the link anytime. The report will also be printable / saveable
              as PDF once opened.
            </p>
            <Button
              data-testid="generate-share-link-btn"
              onClick={create}
              disabled={loading}
              className="btn-brand w-full h-11 rounded-full"
            >
              {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              Generate secure link
            </Button>
          </div>
        ) : (
          <div className="mt-2 space-y-3">
            <div
              className="text-xs break-all p-3 rounded-xl border"
              style={{ background: "#F4EFE6", borderColor: "#E5DEC9", color: "#1C2826" }}
              data-testid="share-url"
            >
              {shareUrl}
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="copy-share-link-btn"
                onClick={copy}
                className="btn-brand rounded-full flex-1 h-11"
              >
                {copied ? (
                  <>
                    <Check size={16} className="mr-2" /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={16} className="mr-2" /> Copy link
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => window.print()}
                className="rounded-full h-11"
                data-testid="print-report-btn"
              >
                <Printer size={16} className="mr-2" /> Print / PDF
              </Button>
            </div>
            <Button
              variant="ghost"
              onClick={revoke}
              disabled={loading}
              className="w-full text-xs rounded-full"
              style={{ color: "#BF5832" }}
              data-testid="revoke-share-link-btn"
            >
              Revoke link
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
